import type { DragEvent } from 'react';
import { useRef, useState } from 'react';

import { TIME_SLOTS, TIME_SNAP_INTERVAL } from '../constants';
import {
  calculateNewTimes,
  checkColumnScheduleTimeOverlap,
  toMinutes,
} from '../helpers';
import type { PreviewBlockState, ProgramBlock, ScheduleType } from '../types';

type UseDragDropProps = {
  scheduleType: ScheduleType;
  blocks: ProgramBlock[];
  setBlocks: React.Dispatch<React.SetStateAction<ProgramBlock[]>>;
  enableDrag: boolean;
  columnHeaders?: string[];
  columnField?: keyof ProgramBlock;
};

export const useDragDrop = ({
  scheduleType,
  blocks,
  setBlocks,
  enableDrag,
  columnHeaders = [],
  columnField = 'instructor',
}: UseDragDropProps) => {
  const [draggedBlock, setDraggedBlock] = useState<ProgramBlock | null>(null);
  const [previewBlock, setPreviewBlock] = useState<PreviewBlockState | null>(
    null
  );
  const dragElementRef = useRef<HTMLDivElement | null>(null);

  /**
   * 驗證拖曳操作是否有效
   */
  const validateDragOperation = (
    newStart: string,
    newEnd: string,
    columnIdx: number
  ) => {
    const endMinutes = toMinutes(newEnd);
    const maxAllowedMinutes = 23 * 60;

    if (endMinutes > maxAllowedMinutes) {
      return { isValid: false, isTimeLimit: true };
    }

    if (scheduleType === 'columnSchedule' && draggedBlock) {
      const targetColumnValue = columnHeaders[columnIdx];
      const existingBlocks = blocks.filter(
        (block) =>
          block[columnField] === targetColumnValue &&
          block.id !== draggedBlock.id
      );

      const hasTimeOverlap = checkColumnScheduleTimeOverlap(
        newStart,
        newEnd,
        existingBlocks,
        draggedBlock.id
      );

      if (hasTimeOverlap) {
        return { isValid: false, isTimeLimit: false };
      }
    }

    return { isValid: true, isTimeLimit: false };
  };

  /**
   * 計算拖曳位置的新時間
   */
  const calculateDropPosition = (
    event: DragEvent<HTMLTableCellElement>,
    timeIdx: number
  ) => {
    if (!draggedBlock) return null;

    const baseTime = TIME_SLOTS[timeIdx];
    const rect = event.currentTarget.getBoundingClientRect();
    const dropY = event.clientY - rect.top;
    const cellHeight = rect.height;

    const { newStart, newEnd } = calculateNewTimes(
      draggedBlock,
      baseTime,
      dropY,
      cellHeight,
      TIME_SNAP_INTERVAL
    );

    // 找出新時間對應的時間索引
    const newTimeIdx = TIME_SLOTS.findIndex(
      (t) =>
        toMinutes(newStart) >= toMinutes(t) &&
        toMinutes(newStart) < toMinutes(t) + 60
    );

    return { newStart, newEnd, newTimeIdx, dropY };
  };

  /**
   * 從事件中提取拖曳的區塊資料
   */
  const extractDropData = (event: DragEvent<HTMLTableCellElement>) => {
    const raw = event.dataTransfer.getData('application/json');
    if (!raw) return null;
    return JSON.parse(raw) as ProgramBlock;
  };

  /**
   * 計算新的拖曳時間
   */
  const calculateNewDropTimes = (
    originalBlock: ProgramBlock,
    event: DragEvent<HTMLTableCellElement>,
    timeIdx: number
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const dropY = event.clientY - rect.top;
    const cellHeight = rect.height;
    const baseTime = TIME_SLOTS[timeIdx];

    return calculateNewTimes(
      originalBlock,
      baseTime,
      dropY,
      cellHeight,
      TIME_SNAP_INTERVAL
    );
  };

  /**
   * 檢查區塊是否移動到不同的欄
   */
  const checkIfBlockMoved = (
    originalBlock: ProgramBlock,
    columnIdx: number
  ) => {
    return scheduleType === 'columnSchedule'
      ? originalBlock[columnField] !== columnHeaders[columnIdx]
      : originalBlock.columnIdx !== columnIdx;
  };

  /**
   * 創建更新後的區塊
   */
  const createUpdatedBlock = (
    originalBlock: ProgramBlock,
    columnIdx: number,
    newStart: string,
    newEnd: string
  ): ProgramBlock => {
    return {
      ...originalBlock,
      ...(scheduleType === 'columnSchedule'
        ? { [columnField]: columnHeaders[columnIdx] }
        : { columnIdx }),
      start: newStart,
      end: newEnd,
    };
  };

  /**
   * 清除拖曳狀態
   */
  const cleanupDragState = () => {
    setDraggedBlock(null);
    setPreviewBlock(null);
    dragElementRef.current = null;
  };

  /**
   * 開始拖曳處理器
   */
  const handleDragStart = (
    event: DragEvent<HTMLDivElement>,
    block: ProgramBlock
  ) => {
    if (!enableDrag) {
      event.preventDefault();
      return;
    }
    setDraggedBlock(block);
    dragElementRef.current = event.currentTarget;
    event.dataTransfer.setData('application/json', JSON.stringify(block));
    event.dataTransfer.effectAllowed = 'move';
  };

  /**
   * 拖曳經過處理器
   */
  const handleDragOver = (
    event: DragEvent<HTMLTableCellElement>,
    columnIdx: number,
    timeIdx: number
  ) => {
    if (!enableDrag) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    event.preventDefault();

    const dropData = calculateDropPosition(event, timeIdx);
    if (!dropData) return;

    const { newStart, newEnd, newTimeIdx, dropY } = dropData;
    const { isValid, isTimeLimit } = validateDragOperation(
      newStart,
      newEnd,
      columnIdx
    );

    if (!isValid) {
      event.dataTransfer.dropEffect = 'none';
      setPreviewBlock({
        columnIdx,
        timeIdx: newTimeIdx,
        newStart,
        newEnd,
        dropY,
        isRestricted: true,
        isTimeLimit,
      });
      return;
    }

    event.dataTransfer.dropEffect = 'move';
    setPreviewBlock({
      columnIdx,
      timeIdx: newTimeIdx,
      newStart,
      newEnd,
      dropY,
      isRestricted: false,
    });
  };

  /**
   * 拖曳離開處理器
   */
  const handleDragLeave = () => {
    if (!enableDrag) return;
    setPreviewBlock(null);
  };

  /**
   * 拖曳放下處理器
   */
  const handleDrop = (
    event: DragEvent<HTMLTableCellElement>,
    columnIdx: number,
    timeIdx: number
  ) => {
    if (!enableDrag) {
      event.preventDefault();
      return;
    }
    event.preventDefault();

    const originalBlock = extractDropData(event);
    if (!originalBlock) return;

    const hasMoved = checkIfBlockMoved(originalBlock, columnIdx);
    const { newStart, newEnd } = calculateNewDropTimes(
      originalBlock,
      event,
      timeIdx
    );

    const endMinutes = toMinutes(newEnd);
    const maxAllowedMinutes = 23 * 60;
    if (endMinutes > maxAllowedMinutes) {
      cleanupDragState();
      return;
    }

    const timeChanged =
      newStart !== originalBlock.start || newEnd !== originalBlock.end;

    if (hasMoved || timeChanged) {
      const updatedBlock = createUpdatedBlock(
        originalBlock,
        columnIdx,
        newStart,
        newEnd
      );

      setBlocks((prev) =>
        prev.map((b) => (b.id === originalBlock.id ? updatedBlock : b))
      );
    }

    cleanupDragState();
  };

  return {
    // 狀態
    draggedBlock,
    previewBlock,
    dragElementRef,

    // 處理器
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  };
};
